import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { FormattedMessage } from 'react-intl'
import KanBoIcon from 'components/KanBoIcon'
import {
  ToggleModal
} from 'components/Modal'
import DateConflicts from '../DateConflicts'
import CardDate from '../../CardDate'
import * as CardStatusRoles from 'const/CardStatusRoles'
import Button from 'components/Button'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import { ModalBoardContext, ModalCardContext } from 'components/Modal'
import requestChangeDueDate from '../../sagas/requestChangeDueDate'
import requestRemoveDueDate from '../../sagas/requestRemoveDueDate'
import { useDispatch, useSelector } from 'react-redux'
import { useCardContext } from 'dataProviders/Card'
import createCardDateConflictsSelector from '../../selectors/createCardDateConflictsSelector'
import createStatusSelector from '../../selectors/createStatusSelector'
import { createSelector } from 'reselect'

const TypedCardDate: any = CardDate
const TypedDateConflicts: any = DateConflicts

const DueDate: React.FC<LeftSidebarProps> = ({
  leftSidebar,
}) => {
  const dispatch = useDispatch()

  const [nowMoment, setNowMoment] = useState(moment())
  const [showModal, setShowModal] = useState(false)

  const cardContext = useCardContext()

  const boardId = useSelector(state => state['view-board'].cards[cardContext.id]?.boardId)
  const isArchived = useSelector(state => state['view-board'].cards[cardContext.id]?.isArchived)
  const isTemplate = useSelector(state => state['view-board'].cards[cardContext.id]?.isTemplate)
  const isBoardClosed = useSelector(state => state['view-board'].view[state['view-board'].cards[cardContext.id]?.boardId]?.isClosed)
  const dueDate = useSelector(state => state['view-board'].cards[cardContext.id]?.dueDate)
  const dueDateId = useSelector(state => state['view-board'].cards[cardContext.id]?.dueDateId)

  const statusSelector = useMemo(() => createStatusSelector((state, { cardId }) => cardId), [])

  const status = useSelector(state => statusSelector(state, {
    cardId: cardContext.id,
  }))

  const dateConflictsSelector = useMemo(() => createCardDateConflictsSelector(), [])

  const dateConflicts = useSelector(state => dateConflictsSelector(state, {
    boardId,
    cardId: cardContext.id,
    dueDate,
  }))

  const dateConflictCountSelector = useMemo(() => createSelector(
    [dateConflictsSelector],
    (dateConflicts) =>
    dateConflicts.reduce((count, errorCard) => count + errorCard.errors.length, 0)
  ), [dateConflictsSelector])

  const dateConflictCount = useSelector(state => dateConflictCountSelector(state, {
    boardId,
    cardId: cardContext.id,
    dueDate,
  }))

  useEffect(() => {
    const timer = setInterval(() => {
      setNowMoment(moment())
    }, 10000)

    return () => {
      clearInterval(timer)
    }
  }, [])

  const handleRemoveDateClick = () => {
    dispatch(requestRemoveDueDate({
      cardId: cardContext.id,
    }))
  }

  const handleDateChange = useCallback((date) => {
    dispatch(requestChangeDueDate({
      cardId: cardContext.id,
      dueDate: date
    }))
  }, [])

  const handleCardsOutOfTimeFrameClick = useCallback((event) => {
    event.stopPropagation()

    leftSidebar.push(() =>
      <ModalBoardContext boardId={boardId}>
        <ModalCardContext cardId={cardContext.id}>
          <DateConflicts
            errorCards={dateConflicts}
            title={
              <FormattedMessage id='CARD_DATE_CONFLICTS_START_DATE' values={{ count: dateConflictCount }} />
            }
          />
        </ModalCardContext>
      </ModalBoardContext>
    )
  }, [leftSidebar, boardId, dateConflicts, dateConflictCount])

  const handleModalToggle = useCallback((show) => {
    if (show === false) {
      setShowModal(false)
    }
  }, [])
  

  let dateStyle: string | undefined = undefined
  if (
    !isArchived &&
    !isTemplate &&
    !isBoardClosed &&
    dueDate &&
    (status
      ? (
        status.role !== CardStatusRoles.NONE &&
        status.role !== CardStatusRoles.COMPLETED
      )
      : false
    )
  ) {
    if (moment(dueDate).isSameOrBefore(nowMoment)) {
      dateStyle = 'danger'
    } else if (moment(dueDate).diff(nowMoment, 'days') <= 7) {
      dateStyle = 'warning'
    }
  }

  return dueDate ? (
    <ToggleModal
      backdrop
      attachClickHandler={false}
      show={showModal}
      onToggle={handleModalToggle}
      attachment='top left'
      targetAttachment='top right'
      constraints={[
        {
          to: 'window',
          pin: true
        }
      ]}
    >
      <TypedCardDate
        id={dueDateId}
        icon={<KanBoIcon name='kanbo-16-due-date' />}
        name={<FormattedMessage id='CARD_DUE_DATE' />}
        style={dateStyle}
        subtitle={
          dateConflicts.length > 0 ? (
            <Button
              bsSize='xsmall'
              bsStyle='warning'
              onClick={handleCardsOutOfTimeFrameClick}
            >
              <FormattedMessage id='CARD_DATE_CONFLICTS' values={{ count: dateConflictCount }} />
            </Button>
          ) : null
        }
        date={dueDate}
        onRemove={handleRemoveDateClick}
        onDateChange={handleDateChange}
      />
      <TypedDateConflicts
        cardId={cardContext.id}
        errorCards={dateConflicts}
        title={
          <FormattedMessage id='CARD_DATE_CONFLICTS_DUE_DATE' values={{ count: dateConflictCount }} />
        }
      />
    </ToggleModal>
  ) : null
}

export default withLeftSidebar(DueDate)
